import React, {useCallback, useEffect, useState} from 'react';
import {Col, Container, Row} from 'reactstrap';
import classNames from 'classnames';
import {Location} from '@reach/router';
import queryString from 'query-string';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faArrowDown} from '@fortawesome/pro-regular-svg-icons';
import {subscribeToCampaign} from 'react-controlled-ab/datalayers/vwo';

import {usePhaseState} from 'funnel-schedule/phase-context';
import useBalanceText from 'utils/useBalanceText';

import PageMeta from 'components/PageMeta';
import Header from 'components/Elements/Header';
import Section, {SectionImage} from 'components/Elements/Section';
import CountdownTimer from 'components/Elements/CountdownTimer';
import Video from 'components/Video';
import IconList, {IconListItem} from 'components/Elements/IconList';
import OptinModal, {OptinModalTrigger} from 'components/Modals/OptinModal';
import ModalExitLanding from 'components/Modals/ModalExitLanding';
import ShareButtons from 'components/Social/ShareButtons';
import Footer from 'components/Elements/Footer';
import Testimonial, {TestimonialBubble} from 'components/Testimonial';

export default function Index(props) {
  return (
    <Location>
      {({location, navigate}) => <MasterClassIndex location={location} navigate={navigate} {...props} />}
    </Location>
  );
}
function MasterClassIndex({
  location = {},
  entry = 'prevent',
  noShare = false,
  noFixedVideo = false,
  noExitIntent = false,
  slug = 'masterclass',
  navigate,
  variant = null,
  pageTitle = 'Unlocking Longevity: Lessons From The World’s Healthiest Elders',
  pageDescription = 'The plant-based keys to a long and fabulously healthy life.',
  ogImage = 'https://cdn.foodrevolution.org/ul/og/ul-opengraph-Prevention_Optimization-Oriented-1200x1200-v20221110.jpg',
  shareUrl = 'https://longevity.foodrevolution.org/masterclass/prevent/',
  facebookDescription = 'Watch this free mini-masterclass to learn the 5 healthy lifestyle habits of people who live an average of 12 to 14 years longer.',
  twitterDescription = 'Watch this free mini-masterclass to learn the 5 healthy lifestyle habits of people who live an average of 12 to 14 years longer.',
  twitterImg = 'https://cdn.foodrevolution.org/ul/og/ul-opengraph-Prevention_Optimization-Oriented-1200x630-20221110.jpg',
  pinterestDescription = 'Watch this free mini-masterclass to learn the 5 healthy lifestyle habits of people who live an average of 12 to 14 years longer.',
  pinterestImg = 'https://cdn.foodrevolution.org/ul/og/ul-opengraph-Prevention_Optimization-Oriented-1000x1500-20221110.jpg',
  ...props
}) {
  const qs = typeof window !== 'undefined' ? queryString.parse(window.location.search) : {};
  const navigateWatch = useCallback(() => navigate('/masterclass/watch/'), []);
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const pathName = window.location.pathname;
      const pathRegex = new RegExp(`\/${slug}\/`, 'g');
      if (!pathName.match(pathRegex)) {
        navigate(`/${slug}/` + location.search);
      }
    }
  }, []);

  const {
    phases: {
      default: defaultSchedule = {
        phase: 'before',
        launchEnd: ''
      }
    }
  } = usePhaseState();

  const launchEnd = new Date(defaultSchedule.launchEnd).getTime();

  function OptinButton({className = null, ...props}) {
    return <OptinModalTrigger className={className}>Watch Now</OptinModalTrigger>;
  }

  function LandingTimer({className = null, timerClass = null, ...props}) {
    return (
      <div className={classNames(`section-countdown countdown-${props.light ? 'light' : 'dark'}`, className)}>
        <h5 className="timer-heading mt-0 mb-2">{defaultSchedule.launchTitle} ends in:</h5>
        <CountdownTimer time={launchEnd} className={timerClass} />
      </div>
    );
  }

  const [groupThreeControl, setGroupThreeControl] = useState(props.groupThreeControl || 'control');
  const [groupFourControl, setGroupFourControl] = useState(props.groupFourControl || 'control');

  useEffect(() => {
    subscribeToCampaign((variantIndex) => {
      switch (variantIndex) {
        case 1:
          setGroupThreeControl('v1');
          break;
      }
    }, 677);
  }, []);
  useEffect(() => {
    subscribeToCampaign((variantIndex) => {
      switch (variantIndex) {
        case 1:
          setGroupThreeControl('v1');
          break;
      }
    }, 685);
  }, []);

  useEffect(() => {
    subscribeToCampaign((variantIndex) => {
      switch (variantIndex) {
        case 1:
          setGroupFourControl('v1');
          break;
      }
    }, 678);
  }, []);
  useEffect(() => {
    subscribeToCampaign((variantIndex) => {
      switch (variantIndex) {
        case 1:
          setGroupFourControl('v1');
          break;
      }
    }, 686);
  }, []);

  useBalanceText();
  return (
    <div className="page d-flex flex-column">
      <PageMeta title={pageTitle} description={pageDescription} url={shareUrl} ogImage={ogImage} />
      <div className="page-content">
        <Header
          className="background-purple"
          logoWhite
          style="dark"
          shareUrl={shareUrl}
          shareImg={ogImage}
          facebookDescription={facebookDescription}
          twitterDescription={twitterDescription}
          twitterImg={twitterImg}
          pinterestDescription={pinterestDescription}
          pinterestImg={pinterestImg}
        />

        <Section
          id="landing-header-cta"
          className={classNames(
            `section-angle-first landing-header-cta landing-header-cta-${entry} landing-header-cta-${entry}-${defaultSchedule.phase}`
          )}
          color="black"
          innerClass="text-white text-center"
          angle="none"
        >
          <Container>
            <Row className={classNames(`d-flex justify-content-center justify-content-lg-end align-items-center`)}>
              <Col xs="12" lg="7">
                <p className="balance-text">
                  <i>FREE Mini-Masterclass Now Playing For A Limited Time</i>
                </p>
                <h2 className="mt-0 mb-3">
                  {
                    {
                      prevent: <>What You Can Learn From The World’s Healthiest Elders</>,
                      reverse: <>How to Feel Better Than You Did 10 Years Ago</>
                    }[entry]
                  }
                </h2>
                <h5 className="mt-0 text-400 mb-4">with John Robbins</h5>
                <div className="d-lg-none">
                  <Row className="d-flex justify-content-center my-4">
                    <Col xs="8" sm="7">
                      <img
                        className="img-fluid border-solid border-white border-5 rounded-circle"
                        src={
                          {
                            prevent: require('static/backgrounds/660654876.jpg'),
                            reverse: require('static/backgrounds/185056863.jpg')
                          }[entry]
                        }
                      />
                    </Col>
                  </Row>
                </div>
                {variant === 'ad' ? null : (
                  <>
                    {defaultSchedule.phase === 'during' ? (
                      <LandingTimer className="countdown-small mb-4" light />
                    ) : null}
                  </>
                )}
                <OptinButton className="mt-4" />
              </Col>
            </Row>
          </Container>
        </Section>

        <Section id="landing-video" color="light-gray" className="text-center" angle="both">
          <Container>
            <Row className="d-flex align-items-center justify-content-center">
              <Col xs="12" lg="10" className="video-col px-0 px-sm-3">
                <div className="px-3 px-sm-0 mb-3">
                  <p className="mb-0">
                    <FontAwesomeIcon icon={faArrowDown} /> Watch the Trailer <FontAwesomeIcon icon={faArrowDown} />
                  </p>
                </div>
                <div>
                  <Video
                    url={
                      {
                        prevent: 'https://www.youtube.com/watch?v=hHsJK06XDmo',
                        reverse: 'https://www.youtube.com/watch?v=B0tcgVUo0Y8'
                      }[entry]
                    }
                    label={
                      {
                        prevent: 'UL - Masterclass Trailer - Prevent/Optimize-oriented',
                        reverse: 'UL - Masterclass Trailer - Reversal-oriented'
                      }[entry]
                    }
                  />
                </div>
              </Col>
            </Row>
          </Container>
        </Section>

        <Section id="landing-discover" color="green">
          <Container>
            <Row>
              <Col>
                <div className="background-white rounded p-4 p-lg-5">
                  {
                    {
                      control: (
                        <>
                          <h2 className="section-heading text-center">Forget “miracle” creams and anti-aging fads.</h2>
                        </>
                      ),
                      v1: null
                    }[groupFourControl]
                  }
                  <h3 className="text-center mt-0">
                    {
                      {
                        prevent: (
                          <>
                            Learn what the longest-living people in the world do to thrive at 90+ — with dramatically
                            reduced odds of cancer, heart disease, dementia, and other chronic diseases.
                          </>
                        ),
                        reverse: (
                          <>
                            Learn what the most significant medical studies tell us about slowing or even reversing the
                            aging process.
                          </>
                        )
                      }[entry]
                    }
                  </h3>
                  <h5>What You’ll Discover Inside This Mini-Masterclass:</h5>
                  <IconList>
                    {
                      {
                        prevent: (
                          <>
                            <IconListItem>
                              5 healthy lifestyle habits that can lead to an average of 12 to 14 years longer lifespan —
                              even if you haven’t always eaten “perfectly”
                            </IconListItem>
                            <IconListItem>
                              Where the world’s healthiest elders live — and what we can all learn from them (it’s not
                              rocket science — but it IS really important to your health and your future!)
                            </IconListItem>
                            <IconListItem>
                              The crucial difference between “lifespan” and “healthspan” - and how you can not just live
                              longer, but live BETTER
                            </IconListItem>
                            <IconListItem>
                              Public enemy #1: The single biggest type of food to minimize or eliminate (find out if
                              you’re eating these if you want to prevent premature aging)
                            </IconListItem>
                            <IconListItem>
                              How to regain the vitality, muscular strength, and even much of the aerobic endurance you
                              might have feared could be gone forever
                            </IconListItem>
                            <IconListItem>
                              One food to eat 3 times a week to reduce chronic inflammation, promote a healthy brain and
                              a healthy microbiome, and dramatically slash your risk of dementia, type 2 diabetes,
                              cardiovascular disease, and even obesity
                            </IconListItem>
                          </>
                        ),
                        reverse: (
                          <>
                            <IconListItem>
                              The crucial difference between “lifespan” and “healthspan” - and how you can not just live
                              longer, but live BETTER
                            </IconListItem>
                            <IconListItem>
                              5 healthy lifestyle habits that can undo signs of aging and lead to an average of 12 to 14
                              years longer lifespan
                            </IconListItem>
                            <IconListItem>
                              Public enemy #1: The single biggest type of food to minimize or eliminate (find out if
                              you’re eating these if you want to prevent premature aging)
                            </IconListItem>
                            <IconListItem>
                              How to regain the vitality, muscular strength, and even much of the aerobic endurance you
                              might have feared could be gone forever
                            </IconListItem>
                            <IconListItem>
                              One food to eat 3 times a week to reduce chronic inflammation, promote a healthy brain and
                              a healthy microbiome, and dramatically slash your risk of dementia, type 2 diabetes,
                              cardiovascular disease, and even obesity
                            </IconListItem>
                            <IconListItem>
                              What to eat and do for a 78% lower risk of developing any chronic disease
                            </IconListItem>
                          </>
                        )
                      }[entry]
                    }
                  </IconList>
                  <h5 className="text-center">All in just 15 minutes!</h5>
                  <div className="text-center">
                    <OptinButton />
                  </div>
                </div>
              </Col>
            </Row>
          </Container>
        </Section>

        <Section id="landing-future" color="white" className="text-center">
          <Container>
            <Row>
              <Col>
                <h2 className="section-only-heading">
                  Join in this free Mini-Masterclass to make sure you aren’t missing any of the essential keys to living
                  a long, healthy, happy life!
                </h2>
              </Col>
            </Row>
          </Container>
        </Section>

        <SectionImage
          id="landing-host"
          className="text-white"
          color="black"
          imgPosition="right"
          imgUrl={require('static/speaker/john-robbins-blueshirt.jpg')}
        >
          <h2 className="section-heading">Your Host, John Robbins</h2>
          <p>
            John Robbins is the author of <i>Diet For A New America,</i> <i>The Food Revolution,</i>{' '}
            <i>Healthy At 100,</i> and 7 other bestsellers. His widespread media attention includes being a featured
            expert on TV shows like Oprah, having his own PBS special about his life and mission, and cover stories and
            features in newspapers including <i>The New York Times</i> and <i>The Washington Post.</i>
          </p>
          <p>
            Today, John is the President and co-founder of Food Revolution Network along with his son, Ocean.Together,
            they conduct the annual Food Revolution Summit where they interview the world’s top doctors, experts, and
            food revolutionaries on what it takes to live an extraordinary life.
          </p>
        </SectionImage>

        {
          {
            control: (
              <SectionImage
                id="landing-cta"
                className="section-angle-last text-white"
                color="purple"
                imgPosition="left"
                imgUrl={require('static/backgrounds/1344505185.jpg')}
              >
                <h3 className="section-heading">There’s a lot at stake.</h3>
                <p>
                  In this 15-minute Mini-Masterclass, you’ll learn why some people grow old in sickness and despair,
                  while others grow old with vitality and inner peace.
                </p>
                <p>
                  Even if you haven’t always taken very good care of yourself, it’s possible to make choices today that
                  will greatly enhance what your future looks like.
                </p>
                <p>But please don’t wait.</p>
                <p>
                  When it comes to healthy aging, the sooner you discover what to do — and what <i>not</i> to do — the
                  better your chances of living a long life with energy to keep doing what you love for many years to
                  come.
                </p>
                <div className="text-center">
                  {variant === 'ad' ? null : (
                    <>{defaultSchedule.phase === 'during' ? <LandingTimer className="mt-4" light /> : null}</>
                  )}
                  <OptinButton className="mt-4" />
                </div>
              </SectionImage>
            ),
            v1: (
              <Section id="landing-cta" color="purple" className="section-angle-last text-center text-white">
                <Container>
                  <Row>
                    <Col>
                      <h2 className="section-heading mb-3">There’s a lot at stake.</h2>
                      <h4 className="mt-0">Please don’t wait.</h4>
                      {variant === 'ad' ? null : (
                        <>{defaultSchedule.phase === 'during' ? <LandingTimer className="mt-4" light /> : null}</>
                      )}
                      <OptinButton className="mt-4" />
                    </Col>
                  </Row>
                </Container>
              </Section>
            )
          }[groupThreeControl]
        }
      </div>

      <div className="footer-content mt-auto">
        <Footer />
        {noShare ? null : (
          <ShareButtons
            shareUrl={shareUrl}
            shareImg={ogImage}
            facebookDescription={facebookDescription}
            twitterDescription={twitterDescription}
            twitterImg={twitterImg}
            pinterestDescription={pinterestDescription}
            pinterestImg={pinterestImg}
            floating
          />
        )}
      </div>

      <OptinModal id="ul-opt" entry={entry} variant={variant} />
      {noExitIntent ? null : <ModalExitLanding entry={entry} />}
    </div>
  );
}
